import React from "react"
import { Trans } from "react-i18next"

import { ConfirmationOverlay } from "shared/ui/confirmation-overlay/ConfirmationOverlay"
import { FileCard } from "shared/ui/file-card/FileCard"
import { Hint } from "shared/ui/hint/Hint"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { zodResolver } from "@hookform/resolvers/zod"
import { LinkedEntitiesList } from "entities/attachments/ui/linked-attachments/LinkedEntitiesList"
import z from "zod"

import { DeleteAttachmentOverlayViewModel } from "./model/useDeleteAttachmentOverlayViewModel"
import "./styles/_delete-attachment-overlay.scss"

const FORM_ID = "delete-attachment-confirmation-form"

const DeleteAttachmentSchema = z.object({
  isConfirmed: z.literal(true)
})

type DeleteAttachmentFormData = {
  isConfirmed: boolean
}

const defaultFormValues: DeleteAttachmentFormData = {
  isConfirmed: false
}

const resolver = zodResolver<
  DeleteAttachmentFormData,
  unknown,
  z.infer<typeof DeleteAttachmentSchema>
>(DeleteAttachmentSchema)

export const DeleteAttachmentOverlayView = React.memo(
  ({
    headline,
    description,
    confirmButtonText,
    fileName,
    fileStem,
    fileExtension,
    linkedEntities,
    onDelete,
    onClose
  }: DeleteAttachmentOverlayViewModel) => {
    return (
      <ConfirmationOverlay.Root
        id="delete-attachment-confirmation-overlay"
        className="delete-attachment-confirmation-overlay"
        classNamePrefix="delete-attachment-confirmation-overlay"
        isDanger
      >
        <ConfirmationOverlay.Header>
          <ConfirmationOverlay.Title>{headline}</ConfirmationOverlay.Title>
          <ConfirmationOverlay.Close onClose={onClose} />
        </ConfirmationOverlay.Header>
        <ConfirmationOverlay.Content>
          <ConfirmationOverlay.Message>
            <p className="delete-attachment-overlay__description">
              {description}
            </p>
            <FileCard
              name={fileStem}
              extension={fileExtension}
              className="delete-attachment-overlay__file-card"
            />
            <LinkedEntitiesList entities={linkedEntities} />
          </ConfirmationOverlay.Message>
          <ConfirmationOverlay.Form<
            DeleteAttachmentFormData,
            z.infer<typeof DeleteAttachmentSchema>
          >
            id={FORM_ID}
            resolver={resolver}
            defaultValues={defaultFormValues}
            onSubmit={onDelete}
          >
            <Hint.Root className="hint--danger">
              <ConfirmationOverlay.CheckboxConfirmation
                name="isConfirmed"
                label={
                  <Trans
                    i18nKey="Attachments.DeleteOverlay.CheckboxLabel"
                    values={{ fileName }}
                    components={{ strong: <span className="font-bold" /> }}
                  />
                }
                className="form-check--danger"
              />
            </Hint.Root>
            <ConfirmationOverlay.Actions>
              <ConfirmationOverlay.Cancel onClick={onClose} />
              <ConfirmationOverlay.Submit form={FORM_ID} icon={IconName.BIN}>
                {confirmButtonText}
              </ConfirmationOverlay.Submit>
            </ConfirmationOverlay.Actions>
          </ConfirmationOverlay.Form>
        </ConfirmationOverlay.Content>
      </ConfirmationOverlay.Root>
    )
  }
)
